/**
 * <ia-notify-me> — PDP "Notify me when available" modal
 * (snippets/ia-notify-me.liquid over the ia-modal shell).
 *
 * Extends IaModal (stacking, Escape, overlay click, body lock). It opens from
 * any [data-notify-trigger="<id>"] on the page through ONE delegated document
 * click, so the triggers that re-render on variant change (the buy row, the
 * sticky bar) never need re-binding. The trigger's data-notify-variant
 * preselects the variant in the modal's select.
 *
 * Submit posts a back-in-stock subscription for the chosen catalog variant
 * (email channel only) to Klaviyo's public Client API — the same request the
 * legacy onsite.js widget made — and shows the inline success / error state.
 * No Klaviyo script is involved.
 *
 * Attributes:
 *   data-drawer-id      modal id (matches data-notify-trigger)
 *   data-company-id     Klaviyo public API key (settings.ia_klaviyo_company_id)
 *   data-error-message  fallback error copy
 *   data-error-invalid  copy for an empty / invalid email
 */
import { IaModal } from 'ia-modal'

const KLAVIYO_BIS_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/'
const KLAVIYO_REVISION = '2024-02-15'
const FOCUSABLE = 'a[href], button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])'

class IaNotifyMe extends IaModal {
  constructor () {
    super()
    this.form = this.querySelector('form')
    this.variantInput = this.querySelector('[name="variant"]')
    this.emailInput = this.querySelector('input[type="email"]')
    this.submitButton = this.querySelector('button[type="submit"]')
    this.errorElement = this.querySelector('[data-notify-error]')
    this.successElement = this.querySelector('[data-notify-success]')
    this.companyId = (this.dataset.companyId || '').trim()
    this.errorMessageDefault = this.dataset.errorMessage || 'Something went wrong. Please try again.'
    this.errorMessageInvalid = this.dataset.errorInvalid || 'Please enter a valid email address.'
    this.onTriggerClick = this.handleTriggerClick.bind(this)

    // Children die with the element: safe to bind once here.
    this.form?.addEventListener('submit', this.handleSubmit.bind(this))
    this.addEventListener('keydown', this.trapFocus.bind(this))
  }

  connectedCallback () {
    super.connectedCallback()
    // remove-then-add: the base class moves this element to <body>, which reruns connect.
    document.removeEventListener('click', this.onTriggerClick)
    document.addEventListener('click', this.onTriggerClick)
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    document.removeEventListener('click', this.onTriggerClick)
  }

  handleTriggerClick (event) {
    const trigger = event.target.closest(`[data-notify-trigger="${this.dataset.drawerId}"]`)
    if (!trigger) return
    event.preventDefault()
    this.trigger = trigger
    this.selectVariant(trigger.dataset.notifyVariant)
    this.open()
  }

  // Only a select can be steered; a hidden input already holds the one sold-out variant.
  selectVariant (variantId) {
    if (!variantId || this.variantInput?.tagName !== 'SELECT') return
    const known = [...this.variantInput.options].some((option) => option.value === variantId)
    if (known) this.variantInput.value = variantId
  }

  open () {
    super.open()
    this.showForm()
    // The shell flips visibility on the is-open class; focus once that style has landed.
    requestAnimationFrame(() => this.emailInput?.focus({ preventScroll: true }))
  }

  close () {
    super.close()
    if (this.trigger?.isConnected) this.trigger.focus()
    this.trigger = null
  }

  // Keep Tab inside the dialog; the shell has no trap of its own.
  trapFocus (event) {
    if (event.key !== 'Tab' || !this.isOpen) return
    const focusable = [...this.querySelectorAll(FOCUSABLE)].filter((element) => element.offsetParent !== null)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  showForm () {
    this.form?.classList.remove('ia-hidden')
    this.errorElement?.classList.add('ia-hidden')
    this.successElement?.classList.add('ia-hidden')
  }

  showError (message) {
    if (!this.errorElement) return
    this.errorElement.textContent = message
    this.errorElement.classList.remove('ia-hidden')
  }

  showSuccess () {
    this.form?.classList.add('ia-hidden')
    this.errorElement?.classList.add('ia-hidden')
    this.successElement?.classList.remove('ia-hidden')
    this.successElement?.querySelector('button')?.focus()
  }

  setBusy (busy) {
    this.form.setAttribute('aria-busy', busy ? 'true' : 'false')
    this.submitButton?.classList.toggle('loading', busy)
    if (this.submitButton) this.submitButton.disabled = busy
  }

  async handleSubmit (event) {
    event.preventDefault()
    if (this.form.getAttribute('aria-busy') === 'true') return

    if (!this.form.checkValidity()) {
      this.showError(this.errorMessageInvalid)
      return
    }
    if (!this.companyId) {
      console.error('ia-notify-me: no Klaviyo public API key set (Theme settings → IA Klaviyo).')
      this.showError(this.errorMessageDefault)
      return
    }

    this.setBusy(true)
    try {
      await this.subscribe(this.emailInput.value.trim(), this.variantInput.value)
      this.showSuccess()
    } catch (error) {
      console.error('ia-notify-me: subscription failed', error)
      this.showError(this.errorMessageDefault)
    } finally {
      this.setBusy(false)
    }
  }

  // Klaviyo answers 202 with an empty body; any non-2xx is a JSON:API error list.
  async subscribe (email, variantId) {
    const response = await fetch(`${KLAVIYO_BIS_URL}?company_id=${encodeURIComponent(this.companyId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } }
          }
        }
      })
    })
    if (response.ok) return
    const detail = await response.json().then((data) => data?.errors?.[0]?.detail).catch(() => null)
    throw new Error(detail || `HTTP ${response.status}`)
  }
}

if (!customElements.get('ia-notify-me')) {
  customElements.define('ia-notify-me', IaNotifyMe)
}
